// The one line chart (ENTRAID-347 redesign, brand/onguard/README.md "Charts"), after Tufte: one or two series, a
// baseline and no gridlines, a direct label at the first and the last value of each series, a sky dot on the current
// point, three x labels (the first date, the middle date and "Today"), no gradient and no per-point color.
//
// Responsive by construction: the lines are an SVG stretched to the plot box (preserveAspectRatio="none" with a
// non-scaling stroke), and every label and marker is HTML positioned in percent, so text keeps its size at any width.
// Accessible: the plot is role="img" with the caller's one-sentence summary, and a visually hidden table holds every
// point. With fewer than 2 values the chart renders nothing, and the caller shows its empty state.
//
// Points are { day: 'YYYY-MM-DD', value: number|null }. A null value is a gap in the line, never a zero.

const DAY_MS = 86400000;
const PAD = 5;

/** Days since the epoch for a 'YYYY-MM-DD' key (UTC, so a daylight saving change never shifts a day). */
export function dayNumber(day) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(day ?? ''));
  if (!m) return null;
  return Math.round(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / DAY_MS);
}

/** The 'YYYY-MM-DD' key for a day number. */
export function dayKey(n) {
  return new Date(n * DAY_MS).toISOString().slice(0, 10);
}

const finite = (n) => typeof n === 'number' && Number.isFinite(n);

/** The y domain: the data range padded by 5 points, clamped to 0 to 100. Null when there is no value. */
export function chartDomain(values, { pad = PAD, min = 0, max = 100 } = {}) {
  const v = values.filter(finite);
  if (v.length === 0) return null;
  const lo = Math.max(min, Math.floor(Math.min(...v) - pad));
  const hi = Math.min(max, Math.ceil(Math.max(...v) + pad));
  return hi > lo ? [lo, hi] : [Math.max(min, lo - 1), Math.min(max, hi + 1)];
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Short date label: "6 Oct" (fixed English month names, the same in every browser). */
export function shortDate(day) {
  const n = dayNumber(day);
  if (n == null) return '';
  const d = new Date(n * DAY_MS);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
}

/** The three x labels: the first day, the middle day and the last day ("Today" when it is today). */
export function xLabels(firstDay, lastDay, todayKey) {
  const a = dayNumber(firstDay);
  const b = dayNumber(lastDay);
  if (a == null || b == null) return [];
  const mid = dayKey(Math.floor((a + b) / 2));
  const last = todayKey && lastDay === todayKey ? 'Today' : shortDate(lastDay);
  const labels = [{ at: 0, text: shortDate(firstDay), align: 'start' }];
  if (b - a >= 2) labels.push({ at: 50, text: shortDate(mid), align: 'middle' });
  labels.push({ at: 100, text: last, align: 'end' });
  return labels;
}

/**
 * Lays out the series on a 0 to 100 percent box: x by date (first to last day), y by the domain (100 is the
 * baseline). Returns null when fewer than 2 values exist across the series.
 */
export function chartLayout(series) {
  const all = series.flatMap((s) => s.points ?? []);
  const values = all.map((p) => p.value).filter(finite);
  if (values.length < 2) return null;
  const days = all.map((p) => dayNumber(p.day)).filter((n) => n != null);
  const first = Math.min(...days);
  const last = Math.max(...days);
  const span = Math.max(1, last - first);
  const domain = chartDomain(values);
  const [lo, hi] = domain;
  const x = (day) => ((dayNumber(day) - first) / span) * 100;
  const y = (v) => 100 - ((v - lo) / (hi - lo)) * 100;
  const laid = series.map((s) => {
    const pts = (s.points ?? []).filter((p) => dayNumber(p.day) != null);
    const segments = [];
    let current = [];
    for (const p of pts) {
      if (finite(p.value)) current.push({ ...p, x: x(p.day), y: y(p.value) });
      else if (current.length) { segments.push(current); current = []; }
    }
    if (current.length) segments.push(current);
    const known = segments.flat();
    return { ...s, segments, first: known[0] ?? null, last: known[known.length - 1] ?? null };
  });
  return { domain, firstDay: dayKey(first), lastDay: dayKey(last), series: laid };
}

const SERIES_COLOR = ['var(--blue)', 'var(--sky)'];

/**
 * @param {{
 *   series: Array<{ name: string, points: Array<{ day: string, value: number|null }>, format?: (v: number) => string }>,
 *   summary: string, todayKey?: string, height?: string, className?: string, testId?: string,
 * }} props
 * `summary` is the accessible name ("onGuard score rose from 77 to 86 over 30 days"); `todayKey` the local
 * 'YYYY-MM-DD' of today, so the last x label reads "Today" only when it is.
 */
export default function LineChart({ series, summary, todayKey, height = 'h-48 sm:h-56', className = '', testId }) {
  const list = (Array.isArray(series) ? series : []).slice(0, 2);
  const layout = chartLayout(list);
  if (!layout) return null;
  const [lo, hi] = layout.domain;
  const labels = xLabels(layout.firstDay, layout.lastDay, todayKey);
  const fmt = (s, v) => (s.format ? s.format(v) : String(Math.round(v)));
  const allDays = [...new Set(list.flatMap((s) => (s.points ?? []).map((p) => p.day)))].sort();

  return (
    <figure className={`m-0 min-w-0 ${className}`.trim()} data-testid={testId} data-line-chart="">
      <div className="flex">
        {/* The domain bounds, muted, at the top and on the baseline. */}
        <div className={`relative w-8 shrink-0 ${height}`} aria-hidden="true">
          <span className="absolute right-2 top-0 -translate-y-1/2 text-xs font-medium text-ipc-muted tabular-nums" data-axis="max">{hi}</span>
          <span className="absolute bottom-0 right-2 translate-y-1/2 text-xs font-medium text-ipc-muted tabular-nums" data-axis="min">{lo}</span>
        </div>
        <div className={`relative min-w-0 flex-1 ${height}`} role="img" aria-label={summary}>
          <svg className="absolute inset-0 h-full w-full overflow-visible" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true" focusable="false">
            <line x1="0" y1="100" x2="100" y2="100" stroke="var(--line)" strokeWidth="2" vectorEffect="non-scaling-stroke" data-baseline="" />
            {layout.series.map((s, si) => s.segments.map((seg, gi) => (
              seg.length > 1 ? (
                <polyline
                  key={`${si}-${gi}`}
                  fill="none"
                  stroke={SERIES_COLOR[si]}
                  strokeWidth="2.5"
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                  points={seg.map((p) => `${p.x},${p.y}`).join(' ')}
                  data-series={si}
                />
              ) : null
            )))}
          </svg>
          {layout.series.map((s, si) => (s.last ? (
            <span
              key={`m${si}`}
              aria-hidden="true"
              className="absolute block h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-sm"
              style={{ left: `${s.last.x}%`, top: `${s.last.y}%`, background: si === 0 ? 'var(--sky)' : 'var(--blue)' }}
              data-end-marker={si}
            />
          ) : null))}
          {layout.series.flatMap((s, si) => [s.first, s.last].map((p, k) => (p ? (
            <span
              key={`l${si}${k}`}
              aria-hidden="true"
              className={`absolute whitespace-nowrap text-sm font-bold tabular-nums ${k === 0 ? 'text-ipc-muted' : 'text-ipc-primary'}`}
              style={{
                left: k === 0 ? `${p.x}%` : undefined,
                right: k === 1 ? `${100 - p.x}%` : undefined,
                top: `${p.y}%`,
                transform: `translate(${k === 1 ? '-6px' : '0'}, ${p.y < 20 ? '6px' : 'calc(-100% - 6px)'})`,
              }}
              data-value-label={k === 0 ? 'first' : 'last'}
              data-series={si}
            >
              {fmt(s, p.value)}
            </span>
          ) : null)))}
        </div>
      </div>
      <div className="relative ml-8 mt-2 h-5 text-xs font-medium text-ipc-muted" aria-hidden="true">
        {labels.map((l) => (
          <span
            key={l.align}
            className="absolute whitespace-nowrap"
            style={{
              left: l.align === 'end' ? undefined : `${l.at}%`,
              right: l.align === 'end' ? 0 : undefined,
              transform: l.align === 'middle' ? 'translateX(-50%)' : undefined,
            }}
            data-x-label={l.align}
          >
            {l.text}
          </span>
        ))}
      </div>
      {/* The data table, visually hidden. A wrapping div carries sr-only: a table ignores the 1 px width and would
          widen a phone's layout viewport. */}
      <div className="sr-only" data-chart-table="">
      <table>
        <caption>{summary}</caption>
        <thead>
          <tr>
            <th scope="col">Date</th>
            {list.map((s) => <th key={s.name} scope="col">{s.name}</th>)}
          </tr>
        </thead>
        <tbody>
          {allDays.map((day) => (
            <tr key={day}>
              <th scope="row">{shortDate(day)}</th>
              {list.map((s) => {
                const p = (s.points ?? []).find((q) => q.day === day);
                return <td key={s.name}>{p && finite(p.value) ? fmt(s, p.value) : 'No data'}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </figure>
  );
}
